import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Check, ChevronDown, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTenant, type SubscriptionPlan } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';

const getPlanBadgeStyles = (plan: SubscriptionPlan): string => {
  switch (plan) {
    case 'pro':
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400';
    case 'premium':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400';
    case 'free':
    default:
      return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
  }
};

const getRoleBadgeStyles = (role: string): string => {
  switch (role) {
    case 'admin':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400';
    case 'game_manager':
      return 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-400';
    case 'reward_manager':
      return 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-400';
    case 'analyst':
      return 'bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-400';
    case 'support':
      return 'bg-pink-100 text-pink-700 dark:bg-pink-900/50 dark:text-pink-400';
    default:
      return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
  }
};

const formatRoleName = (role: string): string => {
  return role
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getPlanDisplayName = (plan: SubscriptionPlan): string => {
  return plan.charAt(0).toUpperCase() + plan.slice(1);
};

const TenantSelector = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { allTenants, selectedTenantId, setSelectedTenantId, isLoadingTenants, canSwitchTenants, userTenantRoles } = useTenant();
  const { isImpersonating, endImpersonation, isLoading: isEndingImpersonation } = useImpersonation();
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.tenant-selector')) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const handleBackToAdmin = async () => {
    await endImpersonation();
    navigate('/admin');
  };

  if (isImpersonating) {
    return (
      <button
        onClick={handleBackToAdmin}
        disabled={isEndingImpersonation}
        className="flex items-center space-x-2 px-3 py-2 text-amber-700 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 transition-colors rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/30 border border-amber-300 dark:border-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="text-sm font-medium hidden sm:inline">
          {isEndingImpersonation ? t('tenant_selector.ending_session') : t('tenant_selector.back_to_admin')}
        </span>
      </button>
    );
  }

  if (!canSwitchTenants || isLoadingTenants) {
    return null;
  }

  const currentTenant = allTenants.find(t => t.id === selectedTenantId);
  const displayName = selectedTenantId === null ? t('tenant_selector.all_tenants') : currentTenant?.name || t('tenant_selector.select_tenant');
  const currentRole = selectedTenantId && !user?.isMegaAdmin ? userTenantRoles.get(selectedTenantId)?.role : null;

  const handleTenantSelect = (tenantId: string | null) => {
    setSelectedTenantId(tenantId);
    setShowMenu(false);
  };

  return (
    <div className="relative tenant-selector">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center space-x-2 px-3 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600"
      >
        <Building2 className="h-4 w-4" />
        <span className="text-sm font-medium hidden sm:inline">{displayName}</span>
        {currentRole && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full hidden sm:inline ${getRoleBadgeStyles(currentRole)}`}>
            {formatRoleName(currentRole)}
          </span>
        )}
        {user?.isMegaAdmin && currentTenant?.subscription_plan && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full hidden sm:inline ${getPlanBadgeStyles(currentTenant.subscription_plan)}`}>
            {getPlanDisplayName(currentTenant.subscription_plan)}
          </span>
        )}
        <ChevronDown className="h-4 w-4" />
      </button>

      {showMenu && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 tenant-menu">
          <div className="p-2">
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-gray-700 mb-2">
              {t('tenant_selector.select_tenant')}
            </div>

            {user?.isMegaAdmin && (
              <>
                <button
                  onClick={() => handleTenantSelect(null)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                    selectedTenantId === null
                      ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Building2 className="h-4 w-4" />
                  <span>{t('tenant_selector.all_tenants')}</span>
                  {selectedTenantId === null && (
                    <Check className="ml-auto h-4 w-4 text-blue-500 dark:text-blue-400" />
                  )}
                </button>
                <div className="my-2 border-t border-gray-100 dark:border-gray-700"></div>
              </>
            )}

            {allTenants.map((tenant) => {
              const tenantRole = userTenantRoles.get(tenant.id);
              return (
                <button
                  key={tenant.id}
                  onClick={() => handleTenantSelect(tenant.id)}
                  className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                    selectedTenantId === tenant.id
                      ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Building2 className="h-4 w-4 mr-3 flex-shrink-0" />
                  <span className="truncate flex-1 text-left">{tenant.name}</span>
                  {!user?.isMegaAdmin && tenantRole && (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ml-2 flex-shrink-0 flex items-center gap-1 ${getRoleBadgeStyles(tenantRole.role)}`}>
                      <Shield className="h-3 w-3" />
                      {formatRoleName(tenantRole.role)}
                    </span>
                  )}
                  {user?.isMegaAdmin && tenant.subscription_plan && (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ml-2 flex-shrink-0 ${getPlanBadgeStyles(tenant.subscription_plan)}`}>
                      {getPlanDisplayName(tenant.subscription_plan)}
                    </span>
                  )}
                  {selectedTenantId === tenant.id && (
                    <Check className="ml-2 h-4 w-4 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantSelector;
